// Parser de códigos de barras EAN-13 emitidos por balanças com etiqueta
// (Ramuza Atena 2, Toledo Prix, Filizola). Padrão brasileiro:
//
//   Modo PESO  — `2 PPPPP WWWWW C`  (PLU 5 + peso em gramas 5 + check)
//   Modo PREÇO — `2 PPPPP VVVVV C`  (PLU 5 + valor em centavos 5 + check)
//
// Formatos alternativos suportados via config:
//   - PLU 4 dígitos: `2 PPPP XXXXXX C`  (com 6 dígitos de peso/valor)
//   - PLU 6 dígitos: `2 PPPPPP XXXXX C`
//   - Prefixo customizável (default '2', mas algumas redes usam '20'-'29')

const DEFAULT_CONFIG = {
  prefix: '2',          // primeiro(s) dígito(s) que marcam etiqueta de balança
  pluDigits: 5,          // 4, 5 ou 6
  mode: 'peso',          // 'peso' (gramas) | 'preco' (centavos)
};

/**
 * Calcula o dígito verificador EAN-13 a partir dos primeiros 12 dígitos.
 * Não é estritamente necessário para nosso uso, mas valida a integridade.
 */
export function ean13CheckDigit(twelveDigits) {
  if (typeof twelveDigits !== 'string' || twelveDigits.length !== 12) return null;
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    const d = parseInt(twelveDigits[i], 10);
    if (Number.isNaN(d)) return null;
    sum += d * (i % 2 === 0 ? 1 : 3);
  }
  return (10 - (sum % 10)) % 10;
}

/**
 * @typedef {Object} BarcodeResult
 * @property {boolean} ok
 * @property {string}  raw                código original
 * @property {string?} prefix
 * @property {string?} plu
 * @property {number?} weightKg           se modo peso
 * @property {number?} priceTotal         se modo preço (em R$)
 * @property {number?} checkDigit
 * @property {boolean} checkValid
 * @property {string}  mode               'peso' | 'preco'
 * @property {string?} reason             motivo da rejeição
 */

/**
 * Tenta parsear um código de balança. Retorna estrutura uniformizada.
 * Se config.mode = 'peso', o resultado traz weightKg.
 * Se config.mode = 'preco', traz priceTotal em R$.
 *
 * @param {string} rawCode
 * @param {Partial<typeof DEFAULT_CONFIG>} cfg
 * @returns {BarcodeResult}
 */
export function parseScaleBarcode(rawCode, cfg = {}) {
  const config = { ...DEFAULT_CONFIG, ...cfg };
  const code = String(rawCode || '').replace(/\D/g, '');

  if (code.length !== 13) {
    return { ok: false, raw: rawCode, reason: `Comprimento inválido: ${code.length} (esperado 13)`, mode: config.mode, checkValid: false };
  }
  if (!code.startsWith(config.prefix)) {
    return { ok: false, raw: rawCode, reason: `Prefixo "${code[0]}" diferente do esperado "${config.prefix}"`, mode: config.mode, checkValid: false };
  }

  const prefixLen = config.prefix.length;
  const pluLen = config.pluDigits;
  const valueLen = 13 - prefixLen - pluLen - 1; // sobra para peso/preço

  if (valueLen < 4 || valueLen > 7) {
    return { ok: false, raw: rawCode, reason: `Configuração inválida: PLU ${pluLen} + valor ${valueLen} não cabe em 13 dígitos`, mode: config.mode, checkValid: false };
  }

  const prefix = code.slice(0, prefixLen);
  const plu = code.slice(prefixLen, prefixLen + pluLen);
  const valueStr = code.slice(prefixLen + pluLen, prefixLen + pluLen + valueLen);
  const checkDigit = parseInt(code[12], 10);

  const expectedCheck = ean13CheckDigit(code.slice(0, 12));
  const checkValid = expectedCheck === checkDigit;

  const result = {
    ok: true,
    raw: rawCode,
    prefix,
    plu,
    checkDigit,
    checkValid,
    mode: config.mode,
  };

  if (config.mode === 'peso') {
    // value em gramas → kg
    result.weightKg = parseInt(valueStr, 10) / 1000;
  } else if (config.mode === 'preco') {
    // value em centavos → R$
    result.priceTotal = parseInt(valueStr, 10) / 100;
  } else {
    return { ok: false, raw: rawCode, reason: `Modo "${config.mode}" desconhecido`, mode: config.mode, checkValid };
  }

  return result;
}

/**
 * Valida superficialmente se um código de barras parece ser de balança
 * (13 dígitos começando com prefixo configurado). Útil pra capturar input
 * de scanner antes de tentar parsear.
 */
export function looksLikeScaleBarcode(rawCode, cfg = {}) {
  const config = { ...DEFAULT_CONFIG, ...cfg };
  const code = String(rawCode || '').replace(/\D/g, '');
  return code.length === 13 && code.startsWith(config.prefix);
}

/**
 * Tenta auto-detectar o formato testando combinações comuns. Retorna o melhor
 * candidato (que tem dígito verificador válido E valores plausíveis).
 */
export function autoDetectFormat(rawCode) {
  const code = String(rawCode || '').replace(/\D/g, '');
  if (code.length !== 13) return null;

  const candidates = [];
  for (const pluDigits of [4, 5, 6]) {
    for (const mode of ['peso', 'preco']) {
      const result = parseScaleBarcode(code, { pluDigits, mode });
      if (!result.ok) continue;
      // Plausibilidade: peso entre 0.001 e 50 kg, preço entre 0.01 e 9999 R$
      const isPlausible =
        (mode === 'peso' && result.weightKg >= 0.001 && result.weightKg <= 50)
        || (mode === 'preco' && result.priceTotal >= 0.01 && result.priceTotal <= 9999.99);
      if (!isPlausible) continue;
      candidates.push({ ...result, _score: (result.checkValid ? 100 : 0) + (isPlausible ? 10 : 0) });
    }
  }
  if (!candidates.length) return null;
  candidates.sort((a, b) => b._score - a._score);
  return candidates[0];
}

export const BARCODE_DEFAULTS = DEFAULT_CONFIG;

/**
 * Classifica uma entrada do leitor/teclado em tipo de código.
 *   - 'card'    : 1 a 4 dígitos (ID de comanda / cartão de mesa)
 *   - 'scale'   : 13 dígitos EAN-13 com prefixo configurado (etiqueta de balança)
 *   - 'ean13'   : 13 dígitos com outro prefixo (produto industrial, pode virar feature futura)
 *   - 'invalid' : nada reconhecido
 *
 * @param {string} raw
 * @param {Partial<typeof DEFAULT_CONFIG>} cfg
 */
export function classifyInput(raw, cfg = {}) {
  const config = { ...DEFAULT_CONFIG, ...cfg };
  const code = String(raw || '').replace(/\D/g, '');
  if (!code) return { kind: 'invalid', reason: 'vazio' };

  // Cartão de comanda: 1 a 4 dígitos
  if (code.length >= 1 && code.length <= 4) {
    return { kind: 'card', cardNumber: parseInt(code, 10), code };
  }

  // EAN-13
  if (code.length === 13) {
    if (code.startsWith(config.prefix)) {
      const scale = parseScaleBarcode(code, config);
      if (scale.ok) return { kind: 'scale', code, scale };
      // tenta autodetect
      const auto = autoDetectFormat(code);
      if (auto && auto.ok) return { kind: 'scale', code, scale: auto };
    }
    return { kind: 'ean13', code };
  }

  return { kind: 'invalid', reason: `Comprimento ${code.length} não reconhecido`, code };
}
